import { FC, ReactNode, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Tooltip,
  TooltipPlacements,
  TooltipTipAlignments
} from 'stubben-edge-unified-frontend-react-components';

type SidebarTooltipProps = {
  label: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  testIdentifier?: string;
};

const SidebarTooltip: FC<SidebarTooltipProps> = ({
  label,
  children,
  disabled = false,
  testIdentifier
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const show = () => {
    if (disabled || label == null || label === '') {
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    setPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 8
    });
    setVisible(true);
  };

  const hide = () => setVisible(false);

  useEffect(() => {
    if (disabled) {
      setVisible(false);
    }
  }, [disabled]);

  return (
    <div
      ref={triggerRef}
      className="w-full"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {visible &&
        createPortal(
          <div
            className="pointer-events-none"
            data-testid={
              testIdentifier
                ? `${testIdentifier}-portal`
                : 'sidebar-tooltip-portal'
            }
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              transform: 'translateY(-50%)',
              zIndex: 60
            }}
          >
            <Tooltip
              placement={TooltipPlacements.Right}
              tipAlignment={TooltipTipAlignments.Center}
              testIdentifier={testIdentifier}
            >
              {label}
            </Tooltip>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SidebarTooltip;
